@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginecardview.layouts

import androidx.compose.runtime.*
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.screens_cus.infraAnzeigeCodeAusMeta
import org.example.project.screens_cus.infraCodeFieldOrder
import org.example.project.screens_cus.infraFieldLabel
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal const val TOR_UEBERSICHT_TEXT_KEY = "torUebersichtText"
internal const val TOR_CODE_SETTINGS_FILE = "toruebersicht_codefields.json"

internal object TorCardStyles : StyleSheet() {
    val card by style {
        width(360.px)
        height(405.px)
        padding(12.px)
        borderRadius(20.px)
        property("box-shadow", "0 14px 28px ${DashboardTheme.SURFACE_SHADOW}")
        backgroundColor(DashboardTheme.SURFACE_CARD)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
        overflow("hidden")
    }
    val cardFree by style {
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
    }
    val cardOccupied by style {
        property("border", "1px solid ${UISettings.ACCENT}")
        property("box-shadow", "0 14px 28px ${DashboardTheme.SURFACE_SHADOW}, 0 0 20px ${UISettings.accentRgba(0.16)}")
    }
    val header by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        minHeight(72.px)
        paddingTop(2.px)
        gap(8.px)
    }
    val torNumber by style {
        fontWeight("800")
        fontSize(30.px)
        color(DashboardTheme.TEXT_LIGHT)
        lineHeight("1.15")
        textAlign("center")
        property("display", "-webkit-box")
        property("-webkit-line-clamp", "2")
        property("-webkit-box-orient", "vertical")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("word-break", "break-word")
    }
    val torTextInput by style {
        width(100.percent)
        height(34.px)
        padding(0.px, 12.px)
        borderRadius(8.px)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        backgroundColor(Color.transparent)
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(15.px)
        boxSizing("border-box")
        property("outline", "none")
    }
    val previewBox by style {
        width(100.percent)
        height(204.px)
        position(Position.Relative)
        flexShrink(0)
        marginTop(4.px)
        borderRadius(16.px)
        property("overflow", "hidden")
        backgroundColor(Color.transparent)
    }
    val previewContent by style {
        position(Position.Absolute)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        padding(16.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        gap(12.px)
        overflowY("auto")
    }
    val entryItem by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
    }
    val entryHeadline by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(8.px)
        fontSize(19.px)
        fontWeight("800")
        lineHeight("1.2")
        color(DashboardTheme.TEXT_LIGHT)
    }
    val entryMeta by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(7.px)
        fontSize(16.px)
        color(DashboardTheme.TEXT_MUTED)
        lineHeight("1.25")
    }
    val metaRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
    }
    val metaValue by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontWeight("700")
        fontSize(16.px)
        textAlign("right")
    }
    val entryDivider by style {
        height(1.px)
        backgroundColor(DashboardTheme.BORDER_LIGHT)
        width(100.percent)
    }
    val bottomBlock by style {
        width(100.percent)
        flexGrow(1)
        minHeight(0.px)
        padding(4.px, 12.px, 10.px, 12.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.SpaceBetween)
        gap(6.px)
        boxSizing("border-box")
    }
    val metaLine by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
        paddingBottom(10.px)
        overflow("hidden")
    }
    val metaText by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(17.px)
        fontWeight("700")
        property("white-space", "nowrap")
    }
    val settingsPanelContent by style {
        flexGrow(1)
        minHeight(0.px)
        padding(18.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(16.px)
        boxSizing("border-box")
    }
    val settingsPanelTitle by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(20.px)
        fontWeight("800")
        lineHeight("1.2")
    }
    val settingsOptionGrid by style {
        display(DisplayStyle.Grid)
        gridTemplateColumns("repeat(3, minmax(0, 1fr))")
        gap(12.px)
    }
    val settingsOptionCard by style {
        minHeight(112.px)
        padding(16.px, 12.px)
        borderRadius(8.px)
        backgroundColor(DashboardTheme.SURFACE_CARD)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(12.px)
        boxSizing("border-box")
        cursor("pointer")
    }
    val settingsOptionLabel by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(14.px)
        fontWeight("700")
        textAlign("center")
    }
    val settingsFooter by style {
        property("margin-top", "auto")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        gap(10.px)
    }
}

@Composable
internal fun TorSettingsPopup(
    fields: Set<String>,
    availableFields: Set<String>,
    onToggle: (String, Boolean) -> Unit,
    onClose: () -> Unit,
    onSave: () -> Unit
) {
    PopupEngineCard(
        windowKey = "tor-settings-popup",
        zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 560,
        heightPx = 330,
        minWidthPx = 460,
        minHeightPx = 300,
        onClose = onClose
    ) {
        Div({ classes(TorCardStyles.settingsPanelContent) }) {
            Div({ classes(TorCardStyles.settingsPanelTitle) }) { Text("Toransicht") }
            Div({ classes(TorCardStyles.settingsOptionGrid) }) {
                infraCodeFieldOrder()
                    .filter { it in availableFields }
                    .forEach { field ->
                        TorSettingsOption(infraFieldLabel(field), field, fields, onToggle)
                    }
            }
            Div({ classes(TorCardStyles.settingsFooter) }) {
                LumaticaButton(
                    label = "Abbrechen",
                    onClick = onClose,
                    variant = LumaticaButtonVariant.Standard,
                    heightPx = 34,
                    paddingH = 12
                )
                LumaticaButton(
                    label = "Speichern",
                    onClick = onSave,
                    variant = LumaticaButtonVariant.Action,
                    heightPx = 34,
                    paddingH = 12
                )
            }
        }
    }
}

@Composable
private fun TorSettingsOption(
    label: String,
    field: String,
    fields: Set<String>,
    onToggle: (String, Boolean) -> Unit
) {
    val checked = field in fields
    Div({
        classes(TorCardStyles.settingsOptionCard)
        onClick { onToggle(field, !checked) }
    }) {
        LumaticaCheckbox(
            checked = checked,
            onChange = { next -> onToggle(field, next) },
            fontSizePx = 26
        )
        Span({ classes(TorCardStyles.settingsOptionLabel) }) { Text(label) }
    }
}

@Composable
fun TorCard(
    tor: All,
    infra: All? = null,
    codeFields: Set<String> = setOf("tor"),
    belegungen: List<All>,
    onTorTextSave: (String) -> Unit
) {
    Style(TorCardStyles)
    var pagerIndex by remember { mutableStateOf(0) }
    var torText by remember(tor.id, tor.notiz) {
        mutableStateOf(tor.notiz)
    }

    fun saveTorText() {
        val normalized = torText.trim()
        if (normalized != tor.notiz) {
            onTorTextSave(normalized)
        }
    }

    LaunchedEffect(belegungen.size) {
        pagerIndex = when {
            belegungen.isNotEmpty() && pagerIndex >= belegungen.size -> belegungen.lastIndex
            belegungen.isEmpty() -> 0
            else -> pagerIndex
        }
    }

    val belegung = belegungen.getOrNull(pagerIndex)
    val isBelegt = belegung != null
    val torNummer = tor.tor?.toString()?.padStart(2, '0').orEmpty()
    val torTitel = "Tor ${torNummer.ifBlank { "-" }}"
    val torCode = tor.torcode.orEmpty().ifBlank {
        infraAnzeigeCodeAusMeta(
            infra = infra,
            typ = "tor",
            values = mapOf(
                "standort" to tor.standort,
                "halle" to tor.halle,
                "tor" to tor.tor?.toString().orEmpty()
            ),
            visibleFields = codeFields
        )
    }
    Div({
        classes(TorCardStyles.card)
        classes(if (isBelegt) TorCardStyles.cardOccupied else TorCardStyles.cardFree)
    }) {
        Div({ classes(TorCardStyles.header) }) {
            Span({ classes(TorCardStyles.torNumber) }) {
                Text(torTitel)
            }
            Input(InputType.Text, attrs = {
                classes(TorCardStyles.torTextInput)
                value(torText)
                attr("maxlength", "80")
                attr("placeholder", "Tor-Notiz")
                onInput { torText = it.value }
                onBlur { saveTorText() }
                onKeyDown {
                    if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                        it.preventDefault()
                        saveTorText()
                    }
                }
            })
        }

        Div({ classes(TorCardStyles.previewBox) }) {
            if (isBelegt && belegungen.isNotEmpty()) {
                Div({ classes(TorCardStyles.previewContent) }) {
                    belegungen.forEachIndexed { index, entry ->
                        if (index > 0) {
                            Div({ classes(TorCardStyles.entryDivider) })
                        }
                        Div({ classes(TorCardStyles.entryItem) }) {
                            Div({ classes(TorCardStyles.entryHeadline) }) {
                                Span { Text("LS: ${entry.lieferscheinnummer.ifBlank { "-" }}") }
                                Span { Text(torUebersichtAuftragsTyp(entry)) }
                            }
                            Div({ classes(TorCardStyles.entryMeta) }) {
                                TorMetaRow("LKW", entry.lkwkennzeichen.ifBlank { "-" })
                                if (torUebersichtAuftragsTyp(entry) == "WA") {
                                    TorMetaRow("Fahrer", entry.fahrername.ifBlank { "-" })
                                    TorMetaRow("Paletten", entry.palettenanzahl.toString())
                                    TorMetaRow("Zielort", torUebersichtZielort(entry).ifBlank { "-" })
                                } else {
                                    TorMetaRow("Paletten", entry.palettenanzahl.toString())
                                    TorMetaRow("Lieferant", entry.lieferant.ifBlank { "-" })
                                }
                            }
                        }
                    }
                }
            }
        }

        Div({ classes(TorCardStyles.bottomBlock) }) {
            Div({ classes(TorCardStyles.metaLine) }) {
                Span({ classes(TorCardStyles.metaText) }) { Text("Halle ${tor.halle.ifBlank { "-" }}") }
                torCode.takeIf { it.isNotBlank() }?.let { code ->
                    Span({ classes(TorCardStyles.metaText) }) { Text("|") }
                    Span({ classes(TorCardStyles.metaText) }) { Text(code) }
                }
            }
        }
    }
}

private fun torUebersichtAuftragsTyp(entry: All): String =
    entry.auftragsart.trim().uppercase()
        .takeIf { it == "WE" || it == "WA" }
        ?: if (entry.lieferant.isNotBlank() && entry.empfaenger.isBlank()) "WE" else "WA"

private fun torUebersichtZielort(entry: All): String =
    listOf(entry.empfaenger, entry.ort, entry.plz)
        .filter { it.isNotBlank() }
        .joinToString(" - ")

@Composable
private fun TorMetaRow(label: String, value: String) {
    Div({ classes(TorCardStyles.metaRow) }) {
        Span { Text(label) }
        Span({ classes(TorCardStyles.metaValue) }) { Text(value) }
    }
}
